import { Link, useParams } from 'react-router-dom';
import { getGuide, GUIDES } from '../data/guides';
import { useSeo, useJsonLd, SITE } from '../utils/seo';

const INK = '#0f172a';
const MUTED = '#64748b';
const INDIGO = '#6366f1';

function GuideHeader() {
  return (
    <nav style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      maxWidth: 1100, margin: '0 auto', padding: '18px 32px',
    }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', fontWeight: 800, fontSize: 16, color: INK }}>
        <span style={{
          display: 'inline-flex', width: 26, height: 26, borderRadius: 7,
          background: `linear-gradient(135deg, ${INDIGO}, #a855f7)`, alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        LE Tech Tools
      </Link>
      <div style={{ display: 'flex', gap: 18, fontSize: 14, fontWeight: 600 }}>
        <Link to="/guides" style={{ color: MUTED, textDecoration: 'none' }}>Guides</Link>
        <Link to="/tools" style={{ color: INDIGO, textDecoration: 'none' }}>All tools →</Link>
      </div>
    </nav>
  );
}

function Guide() {
  const { slug } = useParams();
  const guide = getGuide(slug);

  useSeo({
    title: guide ? `${guide.title} — How It Works | LE Tech Tools` : 'Guide not found | LE Tech Tools',
    description: guide ? guide.description : 'Guides for LE Tech Tools.',
    path: guide ? `/guides/${guide.slug}` : '/guides',
  });

  useJsonLd(guide ? guide.slug : 'notfound', guide && {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Guides', item: `${SITE}/guides` },
          { '@type': 'ListItem', position: 3, name: guide.title, item: `${SITE}/guides/${guide.slug}` },
        ],
      },
      {
        '@type': 'HowTo',
        name: `How the ${guide.title} works`,
        description: guide.description,
        step: guide.steps.map((s, i) => ({
          '@type': 'HowToStep', position: i + 1, name: s.title, text: s.desc,
        })),
      },
    ],
  });

  if (!guide) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <GuideHeader />
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '80px 32px', textAlign: 'center' }}>
          <h1 style={{ fontSize: 26, color: INK }}>Guide not found</h1>
          <p style={{ color: MUTED }}>That guide doesn’t exist. <Link to="/guides" style={{ color: INDIGO }}>Browse all guides →</Link></p>
        </div>
      </div>
    );
  }

  const related = GUIDES.filter(g => g.slug !== guide.slug)
    .sort((a, b) => (a.category === guide.category ? -1 : 0) - (b.category === guide.category ? -1 : 0))
    .slice(0, 4);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', color: INK, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <GuideHeader />
      </div>

      <main style={{ maxWidth: 820, margin: '0 auto', padding: '32px 32px 72px' }}>
        {/* Breadcrumb */}
        <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 18 }}>
          <Link to="/" style={{ color: '#94a3b8', textDecoration: 'none' }}>Home</Link>
          {' / '}
          <Link to="/guides" style={{ color: '#94a3b8', textDecoration: 'none' }}>Guides</Link>
          {' / '}
          <span style={{ color: MUTED }}>{guide.title}</span>
        </div>

        {/* Hero */}
        <div style={{
          display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
          background: '#eef2ff', color: '#4338ca', padding: '4px 10px', borderRadius: 20, border: '1px solid #c7d2fe', marginBottom: 16,
        }}>
          {guide.category}
        </div>
        <h1 style={{ fontSize: 'clamp(28px, 4.5vw, 40px)', fontWeight: 800, letterSpacing: '-0.5px', margin: '0 0 12px', lineHeight: 1.15 }}>
          {guide.title}
        </h1>
        <p style={{ fontSize: 18, color: MUTED, lineHeight: 1.6, margin: '0 0 24px', maxWidth: 640 }}>{guide.tagline}</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
          <Link to={guide.toolPath} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none',
            padding: '13px 26px', borderRadius: 10, fontSize: 15, fontWeight: 700, color: '#fff',
            background: `linear-gradient(135deg, ${INDIGO}, #a855f7)`, boxShadow: '0 6px 20px rgba(99,102,241,.35)',
          }}>
            Launch the {guide.title} →
          </Link>
          <Link to="/guides" style={{
            display: 'inline-flex', alignItems: 'center', padding: '13px 22px', borderRadius: 10,
            fontSize: 15, fontWeight: 600, color: INK, background: '#fff', border: '1px solid #e2e8f0', textDecoration: 'none',
          }}>
            All guides
          </Link>
        </div>

        {/* Intro */}
        <p style={{ fontSize: 16, color: '#334155', lineHeight: 1.75, margin: '0 0 36px' }}>{guide.intro}</p>

        {/* How it works */}
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px', margin: '0 0 20px' }}>How it works</h2>
        <ol style={{ listStyle: 'none', padding: 0, margin: '0 0 40px', counterReset: 'step' }}>
          {guide.steps.map((s, i) => (
            <li key={s.title} style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
              <div style={{
                flexShrink: 0, width: 34, height: 34, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, fontWeight: 800, color: '#fff', background: `linear-gradient(135deg, ${INDIGO}, #a855f7)`,
              }}>
                {i + 1}
              </div>
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', flex: 1 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 5px' }}>{s.title}</h3>
                <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* Features */}
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px', margin: '0 0 16px' }}>What you get</h2>
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 36px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
          {guide.features.map(f => (
            <li key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14.5, color: '#334155', lineHeight: 1.5 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {f}
            </li>
          ))}
        </ul>

        {/* Privacy callout */}
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 20px',
          fontSize: 14.5, color: '#15803d', lineHeight: 1.6, marginBottom: 40,
        }}>
          {guide.privacyNote ? (
            <>{guide.privacyNote}</>
          ) : guide.cloud ? (
            <><strong>Built to be shared.</strong> The {guide.title} is one of two collaborative tools that sync to the cloud so your team sees one live map — access is via a private, unguessable link. Everything else on LE Tech Tools stays 100% in your browser.</>
          ) : guide.network ? (
            <><strong>Local-first — network only when you choose it.</strong> Offline screening in the {guide.title} runs entirely in your browser against lists already cached there; the addresses you paste are never transmitted. Live lookups are opt-in and named in the tool: only the specific address or domain you select is sent, and only to the third-party services listed there.</>
          ) : (
            <><strong>100% local &amp; private.</strong> The {guide.title} runs entirely in your browser. The file you open never leaves your device — no upload, no account, no server.</>
          )}
        </div>

        {/* CTA */}
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '28px', textAlign: 'center', marginBottom: 44,
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>Try the {guide.title}</h2>
          <p style={{ fontSize: 14.5, color: MUTED, margin: '0 0 18px' }}>Free, private, and ready in your browser.</p>
          <Link to={guide.toolPath} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none',
            padding: '13px 28px', borderRadius: 10, fontSize: 15, fontWeight: 700, color: '#fff',
            background: `linear-gradient(135deg, ${INDIGO}, #a855f7)`,
          }}>
            Launch tool →
          </Link>
        </div>

        {/* Related guides — internal linking */}
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 16px' }}>More guides</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          {related.map(g => (
            <Link key={g.slug} to={`/guides/${g.slug}`} style={{
              display: 'block', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', textDecoration: 'none',
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 4 }}>{g.title}</div>
              <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{g.tagline}</div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}

export default Guide;
